import { useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { TeeButton } from '@/components/ui/tee-button';
import { GopherMark } from '@/components/assistant/GopherMark';
import { shortcutLabel } from '@/utils/shortcut';

export interface AssistantSpotlightProps {
  open: boolean;
  anchor: ReactNode;
  onTryIt: () => void;
  onDismiss: () => void;
}

/** One-time "Meet Gopher" callout anchored below the top-bar toggle (D-05). */
export function AssistantSpotlight({ open, anchor, onTryIt, onDismiss }: AssistantSpotlightProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const tryItRef = useRef<HTMLDivElement>(null);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <PopoverAnchor asChild>{anchor}</PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={12}
        role="dialog"
        aria-labelledby={headingId}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          tryItRef.current?.querySelector('button')?.focus();
        }}
        className="relative z-[100] w-[320px] max-w-[calc(100vw-32px)] rounded-[10px] border border-[var(--border-default)] bg-[var(--background-surface-floating)] p-4 shadow-[var(--shadow-popover-lg)] motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
      >
        <span
          aria-hidden="true"
          className="absolute -top-[6px] right-5 h-3 w-3 rotate-45 border-l border-t border-[var(--border-default)] bg-[var(--background-surface-floating)]"
        />
        <div className="flex items-center gap-3">
          <GopherMark size="32px" />
          <h2
            id={headingId}
            className="text-[17px] font-semibold leading-[1.2] text-[var(--text-content-title)]"
          >
            {t('assistant.intro.title')}
          </h2>
        </div>
        <p className="mt-3 text-[14px] font-normal leading-[1.5] text-[var(--text-content-title)]">
          {t('assistant.intro.body', { shortcut: shortcutLabel })}
        </p>
        <div className="mt-3 rounded-[8px] bg-[var(--background-card-elevated)] px-4 py-2 text-[12px] text-[var(--text-content-title)]">
          <span className="font-semibold">{t('assistant.intro.noteTitle')}</span>{' '}
          {t('assistant.intro.noteBody')}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-[12px] text-[var(--text-content-sub-title)]">
            {t('assistant.intro.dismissCaption')}
          </span>
          <div ref={tryItRef}>
            <TeeButton
              variant="primary"
              size="s"
              text={t('assistant.intro.tryIt')}
              onClick={onTryIt}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
